import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useLeadsDashboard, type PeriodComparison } from '@/hooks/useLeadsDashboard';

interface LeadsDashboardProps {
  dateFrom?: Date;
  dateTo?: Date;
  compareEnabled?: boolean;
  onFollowUpFilterClick?: (filter: 'overdue' | 'today' | 'this_week') => void;
}

/** Small "vs previous period" delta line for a pipeline cell */
function DeltaSub({ comparison }: { comparison?: PeriodComparison }) {
  if (!comparison) return null;
  if (comparison.direction === 'neutral') {
    return <span className="text-muted-foreground">— 0% {comparison.label}</span>;
  }
  const isGood = (comparison.direction === 'up') === (comparison.isPositiveGood !== false);
  return (
    <span className={cn('font-medium', isGood ? 'text-success' : 'text-destructive')}>
      {comparison.direction === 'up' ? '↗' : '↘'} {Math.round(comparison.percent)}%
      <span className="font-normal text-muted-foreground"> {comparison.label}</span>
    </span>
  );
}

interface PipelineCellProps {
  label: string;
  value: string | number;
  colorClass: string; // bg-* token class for the 8px square
  sub?: React.ReactNode;
  tooltip?: string;
}

function PipelineCell({ label, value, colorClass, sub, tooltip }: PipelineCellProps) {
  return (
    <div className="px-4 py-3.5" title={tooltip}>
      <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <span className={cn('h-2 w-2 shrink-0 rounded-[2px]', colorClass)} aria-hidden="true" />
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold tracking-tight tabular-nums">{value}</div>
      <div className="mt-0.5 min-h-[15px] text-[11px] leading-[15px] text-muted-foreground">{sub}</div>
    </div>
  );
}

interface FollowUpCardProps {
  label: string;
  value: number;
  borderClass: string; // border-l-* token class
  valueClass: string; // text-* token class
  onClick?: () => void;
}

function FollowUpCard({ label, value, borderClass, valueClass, onClick }: FollowUpCardProps) {
  return (
    <Card
      className={cn('cursor-pointer border-l-[3px] transition-colors hover:bg-muted/50', borderClass)}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      <CardContent className="p-4">
        <div className="text-xs font-medium text-muted-foreground">{label}</div>
        <div className={cn('mt-1 text-2xl font-semibold tracking-tight tabular-nums', valueClass)}>
          {value}
        </div>
      </CardContent>
    </Card>
  );
}

export function LeadsDashboard({ dateFrom, dateTo, compareEnabled = false, onFollowUpFilterClick }: LeadsDashboardProps) {
  const { data: metrics, isLoading } = useLeadsDashboard(dateFrom, dateTo, compareEnabled);

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-[102px] rounded-lg" />
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-[86px] rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (!metrics) return null;

  const cmp = metrics.comparisons;

  return (
    <div className="space-y-3">
      {/* Pipeline strip — where leads stand */}
      <Card>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 lg:divide-x lg:divide-border">
          <PipelineCell
            label="New"
            value={metrics.totalLeadsOpen}
            colorClass="bg-info"
            sub={<DeltaSub comparison={cmp?.totalLeadsOpen} />}
          />
          <PipelineCell
            label="Follow-up"
            value={metrics.leadsNeedFollowUp}
            colorClass="bg-warning"
            sub={<DeltaSub comparison={cmp?.leadsNeedFollowUp} />}
          />
          <PipelineCell
            label="Estimate sent"
            value={metrics.leadsEstimateSent}
            colorClass="bg-primary"
            sub={<DeltaSub comparison={cmp?.leadsEstimateSent} />}
          />
          <PipelineCell
            label="Booked"
            value={metrics.leadsConverted}
            colorClass="bg-success"
            sub={<DeltaSub comparison={cmp?.leadsConverted} />}
          />
          <PipelineCell
            label="Lost"
            value={metrics.leadsLost}
            colorClass="bg-destructive"
            sub={<DeltaSub comparison={cmp?.leadsLost} />}
          />
          <PipelineCell
            label="Conversion"
            value={`${metrics.conversionRate.toFixed(1)}%`}
            colorClass="bg-primary"
            tooltip="Converted leads / (Total leads - Lost leads). Lost leads are excluded from the denominator."
            sub={
              cmp?.conversionRate ? (
                <DeltaSub comparison={cmp.conversionRate} />
              ) : (
                `${metrics.leadsConverted} of ${metrics.leadsConverted + (metrics.totalLeadsOpen || 0)} eligible`
              )
            }
          />
        </div>
      </Card>

      {/* Follow-up actions — what needs attention */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <FollowUpCard
          label="Overdue follow-ups"
          value={metrics.followUpsOverdue}
          borderClass="border-l-destructive"
          valueClass="text-destructive"
          onClick={() => onFollowUpFilterClick?.('overdue')}
        />
        <FollowUpCard
          label="Due today"
          value={metrics.followUpsDueToday}
          borderClass="border-l-warning"
          valueClass="text-warning"
          onClick={() => onFollowUpFilterClick?.('today')}
        />
        <FollowUpCard
          label="Due this week"
          value={metrics.followUpsThisWeek}
          borderClass="border-l-info"
          valueClass="text-info"
          onClick={() => onFollowUpFilterClick?.('this_week')}
        />
      </div>
    </div>
  );
}
